import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout.jsx';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  FileText, 
  Users, 
  Bell, 
  TrendingUp, 
  AlertTriangle,
  Upload,
  ArrowRight,
  Activity,
  Calendar,
  Stethoscope
} from 'lucide-react';
import { getHealthSummary, getReminders, getFamilyMembers, getHealthTrends, getWearableSummary } from '@/services/api';
import { toast } from 'sonner';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

export default function DashboardPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [trends, setTrends] = useState({});
  const [wearableSummary, setWearableSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [summaryData, remindersData, membersData, trendsData, wearableData] = await Promise.all([
        getHealthSummary(),
        getReminders(),
        getFamilyMembers(),
        getHealthTrends(),
        getWearableSummary()
      ]);
      setSummary(summaryData);
      setReminders(remindersData.filter(r => !r.is_completed).slice(0, 5));
      setFamilyMembers(membersData);
      setTrends(trendsData);
      setWearableSummary(wearableData);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const upcomingReminders = reminders.filter(r => {
    const reminderDate = new Date(r.reminder_date);
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    return reminderDate <= nextWeek;
  });

  const trendMetricKeys = Object.keys(trends || {});
  const primaryMetric = trendMetricKeys[0];
  const trendData = primaryMetric
    ? (trends[primaryMetric] || []).map(item => ({
        date: item.date,
        value: item.value
      }))
    : [];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="patient-dashboard">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome back, {user?.full_name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-600 mt-1">Here's an overview of your health records</p>
          </div>
          <Link to="/upload" data-testid="upload-record-link">
            <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg px-6 btn-active" data-testid="upload-record-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload Record
            </Button>
          </Link>
        </div>

        {/* Stats Grid - Bento Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Total Records */}
          <Card className="bg-white border-slate-100 shadow-sm card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wide">Total Records</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {loading ? '—' : summary?.total_records || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-900" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Family Members */}
          <Card className="bg-white border-slate-100 shadow-sm card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wide">Family Members</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {loading ? '—' : familyMembers.length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Reminders */}
          <Card className="bg-white border-slate-100 shadow-sm card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wide">Upcoming Reminders</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {loading ? '—' : upcomingReminders.length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Bell className="w-6 h-6 text-amber-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Abnormal Findings */}
          <Card className={`bg-white border-slate-100 shadow-sm card-hover ${summary?.abnormal_findings?.length > 0 ? 'metric-abnormal' : ''}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wide">Attention Needed</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {loading ? '—' : summary?.abnormal_findings?.length || 0}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${summary?.abnormal_findings?.length > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                  <AlertTriangle className={`w-6 h-6 ${summary?.abnormal_findings?.length > 0 ? 'text-red-600' : 'text-slate-400'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Abnormal Findings - Large Card */}
          <Card className="md:col-span-2 bg-white border-slate-100 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Recent Health Alerts
              </CardTitle>
              <Link to="/records" data-testid="view-records-link">
                <Button variant="ghost" size="sm" className="text-blue-900 hover:text-blue-700" data-testid="view-records-btn">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="skeleton h-16 rounded-lg" />
                  ))}
                </div>
              ) : summary?.abnormal_findings?.length > 0 ? (
                <div className="space-y-3">
                  {summary.abnormal_findings.slice(0, 5).map((finding, index) => (
                    <div 
                      key={index} 
                      className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 animate-fade-in"
                      style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                    >
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-slate-900 font-medium">{finding.finding}</p>
                        <p className="text-sm text-slate-500 mt-1">{finding.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Activity className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-slate-600">No health alerts at this time</p>
                  <p className="text-sm text-slate-400 mt-1">All your metrics look normal!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Reminders */}
          <Card className="bg-white border-slate-100 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Upcoming Reminders
              </CardTitle>
              <Link to="/reminders" data-testid="view-reminders-link">
                <Button variant="ghost" size="sm" className="text-blue-900 hover:text-blue-700" data-testid="view-reminders-btn">
                  View All
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="skeleton h-14 rounded-lg" />
                  ))}
                </div>
              ) : upcomingReminders.length > 0 ? (
                <div className="space-y-3">
                  {upcomingReminders.map((reminder, index) => (
                    <div 
                      key={reminder.id} 
                      className="p-3 bg-amber-50 border border-amber-100 rounded-xl animate-fade-in"
                      style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-slate-900 font-medium truncate">{reminder.title}</p>
                          <p className="text-sm text-slate-500">{new Date(reminder.reminder_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No upcoming reminders</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Trend Snapshot */}
        <Card className="bg-white border-slate-100 shadow-sm" data-testid="trend-snapshot-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Trend Snapshot
              </CardTitle>
              <p className="text-sm text-slate-500">
                {primaryMetric ? `Latest ${primaryMetric} readings` : 'No metrics tracked yet'}
              </p>
            </div>
            <Link to="/analytics" data-testid="trend-view-full-link">
              <Button variant="ghost" size="sm" className="text-blue-900 hover:text-blue-700" data-testid="trend-view-full-btn">
                View Analytics
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="skeleton h-56 rounded-xl" />
            ) : trendData.length > 1 ? (
              <div className="h-56 chart-container rounded-lg p-2" data-testid="trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1e3a8a" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#1e3a8a" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Area type="monotone" dataKey="value" stroke="#1e3a8a" strokeWidth={2} fill="url(#trendGradient)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-8" data-testid="trend-empty-state">
                <TrendingUp className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">Upload records to see health trends.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-100 shadow-sm" data-testid="daily-health-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Daily Health Monitoring
              </CardTitle>
              <p className="text-sm text-slate-500">Wearable data is tracked separately from medical records.</p>
            </div>
            <Link to="/daily-health" data-testid="daily-health-link">
              <Button variant="ghost" size="sm" className="text-blue-900 hover:text-blue-700" data-testid="daily-health-btn">
                Open Daily Health
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {wearableSummary?.latest ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="daily-health-latest">
                <div>
                  <p className="text-xs text-slate-500">Steps</p>
                  <p className="text-lg font-semibold text-slate-900" data-testid="daily-health-steps">{wearableSummary.latest.steps ?? '--'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Sleep (hrs)</p>
                  <p className="text-lg font-semibold text-slate-900" data-testid="daily-health-sleep">{wearableSummary.latest.sleep_hours ?? '--'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Heart Rate</p>
                  <p className="text-lg font-semibold text-slate-900" data-testid="daily-health-heart">{wearableSummary.latest.heart_rate_avg ?? '--'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Calories</p>
                  <p className="text-lg font-semibold text-slate-900" data-testid="daily-health-calories">{wearableSummary.latest.calories ?? '--'}</p>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500" data-testid="daily-health-empty">
                No wearable metrics yet. Add your first daily entry.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/upload" className="block" data-testid="quick-action-upload">
            <Card className="bg-white border-slate-100 shadow-sm card-hover h-full">
              <CardContent className="p-5 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-3">
                  <Upload className="w-6 h-6 text-blue-900" />
                </div>
                <p className="font-medium text-slate-900">Upload Record</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/analytics" className="block" data-testid="quick-action-analytics">
            <Card className="bg-white border-slate-100 shadow-sm card-hover h-full">
              <CardContent className="p-5 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-3">
                  <TrendingUp className="w-6 h-6 text-green-700" />
                </div>
                <p className="font-medium text-slate-900">View Trends</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/family" className="block" data-testid="quick-action-family">
            <Card className="bg-white border-slate-100 shadow-sm card-hover h-full">
              <CardContent className="p-5 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
                  <Users className="w-6 h-6 text-purple-700" />
                </div>
                <p className="font-medium text-slate-900">Manage Family</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/consultations" className="block" data-testid="quick-action-consultations">
            <Card className="bg-white border-slate-100 shadow-sm card-hover h-full">
              <CardContent className="p-5 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
                  <Stethoscope className="w-6 h-6 text-amber-700" />
                </div>
                <p className="font-medium text-slate-900">Consultations</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}
